import { useState, useEffect, useContext, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { NotificationContext } from '../../../contexts/notificationContext'
import usePermission from '../../../hooks/usePermission'
import {
    fetchIssues,
    createIssue,
    updateIssue,
    deleteIssue,
    uploadAttachment,
} from '../services/issueService'

export const useIssueManagement = () => {
    const [searchParams] = useSearchParams()
    const { addIssueNotification } = useContext(NotificationContext)

    // State management
    const [issues, setIssues] = useState([])
    const [filteredIssues, setFilteredIssues] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedIssue, setSelectedIssue] = useState(null)
    const [showForm, setShowForm] = useState(false)
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
    const [showDetail, setShowDetail] = useState(false)
    const [issueToDelete, setIssueToDelete] = useState(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [editMode, setEditMode] = useState(false)

    // Filters
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [priorityFilter, setPriorityFilter] = useState('all')
    const [plantFilter, setPlantFilter] = useState([])
    const [escalationFilter, setEscalationFilter] = useState('all')
    const [issueTypeFilter, setIssueTypeFilter] = useState('all')
    const [categoryFilter, setCategoryFilter] = useState('all')
    const [assignedToFilter, setAssignedToFilter] = useState('all')
    const [dateRange, setDateRange] = useState({
        from: dayjs().subtract(30, 'day'),
        to: dayjs(),
    })

    // View State (Tabs/Sort)
    const [tab, setTab] = useState(0)
    const [isExporting, setIsExporting] = useState(false)
    const [sortBy, setSortBy] = useState('plantName')
    const [sortOrder, setSortOrder] = useState('asc')

    // Constants
    const statusOptions = [
        'Open', 'Under Review', 'Material Awaited', 'OEM',
        'In Progress', 'Resolved', 'Closed', 'Deferred'
    ]
    const priorityOptions = ['High', 'Medium', 'Low']
    const issueTypeOptions = ['Corrective', 'Preventive', 'Warranty', 'Administrative']
    const categoryOptions = [
        'Electrical', 'Communication / RMS', 'Structural / Mounting', 'Cleaning / Soiling',
        'Civil / Roof Related', 'Earthing / Lightning', 'Safety', 'Regulatory / DISCOM',
        'Vendor / OEM', 'Reporting / Documentation', 'Others',
    ]
    const escalationOptions = [
        { label: 'L1 – Technician', value: 'Level 1' },
        { label: 'L2 – Supervisor', value: 'Level 2' },
        { label: 'L3 – Central Team', value: 'Level 3' },
        { label: 'L4 – Vendor', value: 'Level 4' },
        { label: 'L5 – Management', value: 'Level 5' },
    ]
    const assignedToOptions = [
        'Pallavi Band', 'Ramsing Rajput', 'Abhishek Deshmukh', 'Ujjawala Thakare',
        'Vivekanada Yadav', 'Suraj Yadav', 'Dheerendra Yadav', 'Sanket Jadhav',
        'Obeliwe Molepa', 'Shahid Anwar', 'Shreyas Kumbhar',
    ]

    // Get unique plants from issues
    const plantOptions = useMemo(() => {
        const uniquePlants = [
            ...new Set(issues.map((issue) => issue.plantId).filter(Boolean)),
        ]
        return uniquePlants.map((plantId) => {
            const issue = issues.find((i) => i.plantId === plantId)
            return {
                id: plantId,
                name: issue?.plantName || plantId,
            }
        })
    }, [issues])

    // Initialize filters from URL parameters
    useEffect(() => {
        const plantId = searchParams.get('plantId')

        if (plantId) {
            setPlantFilter([plantId])
            setTab(1) // switch to table tab
        }
    }, [searchParams])

    // Load issues on component mount
    useEffect(() => {
        loadIssues(false)
    }, [])

    // Poll for updates
    useEffect(() => {
        if (showForm || showDetail || showDeleteModal) return

        const intervalId = setInterval(() => {
            loadIssues(true)
        }, 60000)

        return () => clearInterval(intervalId)
    }, [showForm, showDetail, showDeleteModal])

    // Filter effect
    useEffect(() => {
        filterIssues()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        issues, searchTerm, statusFilter, priorityFilter, escalationFilter,
        plantFilter, issueTypeFilter, categoryFilter, dateRange,
        assignedToFilter, sortBy, sortOrder,
    ])

    const loadIssues = async (isBackground = false) => {
        try {
            if (!isBackground) {
                setLoading(true)
            }
            // Fetch all issues so client-side pagination works correctly
            const data = await fetchIssues()
            const normalised = data.map((issue) => ({
                ...issue,
                id: issue.id?.$oid || issue.id || issue.id || null,
            }))
            setIssues(normalised)
        } catch (error) {
            console.error('Error loading issues:', error)
        } finally {
            if (!isBackground) {
                setLoading(false)
            }
        }
    }

    const filterIssues = () => {
        let filtered = [...issues]

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(
                (issue) =>
                    (issue.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (issue.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (issue.issueId || '').toLowerCase().includes(searchTerm.toLowerCase())
            )
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter((issue) => issue.status === statusFilter)
        }

        // Priority filter
        if (priorityFilter !== 'all') {
            filtered = filtered.filter((issue) => issue.priority === priorityFilter)
        }

        // Plant filter
        if (plantFilter.length > 0) {
            filtered = filtered.filter((issue) => plantFilter.includes(issue.plantId))
        }

        // Escalation filter
        if (escalationFilter !== 'all') {
            filtered = filtered.filter((issue) => (issue.escalationLevel || '') === escalationFilter)
        }

        // Issue Type filter
        if (issueTypeFilter !== 'all') {
            filtered = filtered.filter((issue) => issue.issueType === issueTypeFilter)
        }

        // Category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter((issue) => issue.category === categoryFilter)
        }

        // Assigned To filter
        if (assignedToFilter !== 'all') {
            filtered = filtered.filter((issue) => issue.assignedTo === assignedToFilter)
        }

        // Date range filter
        filtered = filtered.filter((issue) => {
            const issueDate = dayjs(issue.createdAt)
            return (
                issueDate.isAfter(dateRange.from) &&
                issueDate.isBefore(dateRange.to.add(1, 'day'))
            )
        })

        // Sorting
        if (sortBy) {
            filtered.sort((a, b) => {
                const aValue = a[sortBy] || ''
                const bValue = b[sortBy] || ''

                if (sortOrder === 'asc') {
                    return aValue.toString().localeCompare(bValue.toString())
                } else {
                    return bValue.toString().localeCompare(aValue.toString())
                }
            })
        }

        setFilteredIssues(filtered)
        console.log('Filtered Issues Count:', filtered.length)
    }

    const handleTabChange = (event, newValue) => {
        setTab(newValue)
    }

    const handleSort = (column) => {
        if (sortBy === column) {
            if (sortOrder === 'asc') {
                setSortOrder('desc')
            } else if (sortOrder === 'desc') {
                setSortBy(null)
                setSortOrder('asc')
            }
        } else {
            setSortBy(column)
            setSortOrder('asc')
        }
    }

    // Action Handlers
    const handleCreateIssue = () => {
        setSelectedIssue(null)
        setEditMode(false)
        setShowForm(true)
    }

    const handleEditIssue = (issue) => {
        setSelectedIssue({
            ...issue,
            id: issue.id?.$oid || issue.id || issue.id || null,
        })
        setEditMode(true)
        setShowForm(true)
    }

    const handleViewIssue = (issue) => {
        setSelectedIssue({
            ...issue,
            id: issue.id?.$oid || issue.id || issue.id || null,
        })
        setShowDetail(true)
    }

    const handleDeleteIssue = (issue) => {
        const issueId = issue.id?.$oid || issue.id || issue.id
        if (!issueId) return
        setIssueToDelete({ ...issue, id: issueId })
        setShowDeleteModal(true)
    }

    const confirmDeleteIssue = async () => {
        if (!issueToDelete) return

        try {
            await deleteIssue(issueToDelete.id)
            setSnackbar({
                open: true,
                message: 'Issue deleted successfully!',
                severity: 'success'
            })
            await loadIssues()
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to delete issue: ' + error.message,
                severity: 'error'
            })
        } finally {
            setShowDeleteModal(false)
            setIssueToDelete(null)
        }
    }

    const handleCancelDelete = () => {
        setShowDeleteModal(false)
        setIssueToDelete(null)
    }

    const handleFormSubmit = async (issueData, attachments = []) => {
        try {
            let createdOrUpdatedIssue

            if (editMode && selectedIssue) {
                await updateIssue(selectedIssue.id, issueData)
                createdOrUpdatedIssue = selectedIssue

                // Upload new attachments
                const newAttachments = attachments.filter(att => !att.isUploaded && att.file)
                if (newAttachments.length > 0) {
                    for (const att of newAttachments) {
                        try {
                            await uploadAttachment(selectedIssue.id, att.file)
                        } catch (e) { console.error('Attachment upload failed', e) }
                    }
                }
            } else {
                // Create new
                const response = await createIssue(issueData)
                createdOrUpdatedIssue = response

                const issueId = createdOrUpdatedIssue?.id || createdOrUpdatedIssue?.issueId || createdOrUpdatedIssue?._id

                // Upload attachments
                const attachmentsToUpload = attachments.filter(att => !!att.file && !att.isUploaded)
                if (attachmentsToUpload.length > 0 && issueId) {
                    for (const att of attachmentsToUpload) {
                        try {
                            await uploadAttachment(issueId, att.file)
                        } catch (e) { console.error('Attachment upload failed', e) }
                    }
                }

                // Notifications
                if (
                    createdOrUpdatedIssue &&
                    (issueData.priority === 'High' || issueData.priority === 'Medium')
                ) {
                    addIssueNotification({
                        id: issueId,
                        title: issueData.title,
                        priority: issueData.priority,
                        plantId: issueData.plantId,
                        plantName: issueData.plantName || 'Unknown Plant',
                    })
                }
            }

            setSnackbar({
                open: true,
                message: editMode ? 'Issue updated successfully!' : 'Issue created successfully!',
                severity: 'success'
            })

            setShowForm(false)
            setSelectedIssue(null)
            await loadIssues()
        } catch (error) {
            console.error('Error saving issue:', error)
            setSnackbar({
                open: true,
                message: `Failed to ${editMode ? 'update' : 'create'} issue: ${error.message}`,
                severity: 'error'
            })
        }
    }

    const handleFormClose = () => {
        setShowForm(false)
        setSelectedIssue(null)
        setEditMode(false)
    }

    const handleDetailClose = () => {
        setShowDetail(false)
        setSelectedIssue(null)
    }

    // Export Logic
    const convertToCSV = (data, columns) => {
        const headers = columns.map(col => `"${col.header}"`).join(',')
        const rows = data.map(item => {
            return columns.map(col => {
                const value = col.accessor(item)
                if (value === null || value === undefined) return '""'
                const stringValue = String(value).replace(/"/g, '""')
                return `"${stringValue}"`
            }).join(',')
        })
        return [headers, ...rows].join('\n')
    }

    const downloadCSV = (csvContent, filename) => {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const exportToCSV = () => {
        if (filteredIssues.length === 0) return

        setIsExporting(true)
        try {
            const exportColumns = [
                { header: 'Site Name', accessor: (issue) => issue.plantName || 'N/A' },
                { header: 'Plant Capacity (kWp)', accessor: (issue) => issue.plantCapacity || 'N/A' },
                { header: 'Issue', accessor: (issue) => issue.title || 'N/A' },
                { header: 'Category', accessor: (issue) => issue.category || 'N/A' },
                { header: 'Priority', accessor: (issue) => issue.priority || 'N/A' },
                { header: 'Issue Type', accessor: (issue) => issue.issueType || 'N/A' },
                {
                    header: 'Date Reported', accessor: (issue) =>
                        issue.createdAt ? dayjs(issue.createdAt).format('DD-MM-YYYY') : 'N/A'
                },
                { header: 'Assigned To (Internal)', accessor: (issue) => issue.assignedTo || 'N/A' },
                {
                    header: 'Expected Closure Date', accessor: (issue) =>
                        issue.expectedClosingDate ? dayjs(issue.expectedClosingDate).format('DD-MM-YYYY') : 'N/A'
                },
                {
                    header: 'Actual Closure Date', accessor: (issue) =>
                        issue.actualClosingDate ? dayjs(issue.actualClosingDate).format('DD-MM-YYYY') : 'N/A'
                },
                { header: 'Status', accessor: (issue) => issue.status || 'N/A' },
                { header: 'Delay', accessor: (issue) => issue.delayDays || 'N/A' },
                { header: 'Impact', accessor: (issue) => issue.severityImpact || 'N/A' },
                { header: 'Escalation Level', accessor: (issue) => issue.escalationLevel || 'N/A' },
                {
                    header: 'Customer Communication', accessor: (issue) =>
                        typeof issue.customerCommunicationDone === 'boolean'
                            ? (issue.customerCommunicationDone ? 'Y' : 'N')
                            : 'N/A'
                },
                { header: 'Description', accessor: (issue) => issue.description || 'N/A' },
                { header: 'Root Cause', accessor: (issue) => issue.rootCausePreliminary || 'N/A' },
                { header: 'Action Plan', accessor: (issue) => issue.nextActionFollowUpPlan || 'N/A ' }
            ]

            const csvData = convertToCSV(filteredIssues, exportColumns)
            const timestamp = dayjs().format('YYYY-MM-DD_HH-mm-ss')
            const plantFilterText = plantFilter.length > 0 ? `${plantFilter.length}plants` : ''
            const filename = `issues_${filteredIssues.length}_${plantFilterText}_${timestamp}.csv`

            downloadCSV(csvData, filename)
        } catch (error) {
            console.error('Error exporting to CSV:', error)
        } finally {
            setIsExporting(false)
        }
    }

    // Helper Styles
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'High': return 'error'
            case 'Medium': return 'warning'
            case 'Low': return 'success'
            default: return 'default'
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'Open': return 'error'
            case 'In Progress': return 'secondary'
            case 'OEM': return 'info'
            case 'Material Awaited': return 'warning'
            case 'Resolved': return 'success'
            case 'Closed': return 'default'
            default: return 'default'
        }
    }

    const getEscalationLevelStyle = (level) => {
        const levelMap = {
            'Level 1': { color: 'success', label: 'L1 – Technician', icon: '🧰' },
            'Level 2': { color: 'info', label: 'L2 – Supervisor', icon: '⚙️' },
            'Level 3': { color: 'warning', label: 'L3 – Central Team', icon: '📊' },
            'Level 4': { color: 'error', label: 'L4 – Vendor', icon: '🏭' },
            'Level 5': { color: 'secondary', label: 'L5 – Management', icon: '🚨' },
        }
        return levelMap[level] || { color: 'default', label: level || 'N/A', icon: '❓' }
    }

    const { is, can } = usePermission()

    return {
        state: {
            issues, filteredIssues, loading, selectedIssue, showForm, snackbar, showDetail,
            issueToDelete, showDeleteModal, editMode, tab, isExporting,
            plantOptions,
            // filters
            searchTerm, statusFilter, priorityFilter, plantFilter, escalationFilter,
            issueTypeFilter, categoryFilter, assignedToFilter, dateRange, sortBy, sortOrder
        },
        setters: {
            setSearchTerm, setStatusFilter, setPriorityFilter, setPlantFilter, setEscalationFilter,
            setIssueTypeFilter, setCategoryFilter, setAssignedToFilter, setDateRange, setSortBy, setSortOrder,
            setTab, setSnackbar, setShowDetail
        },
        actions: {
            handleCreateIssue, handleEditIssue, handleViewIssue, handleDeleteIssue,
            confirmDeleteIssue, handleCancelDelete, handleFormSubmit, handleFormClose,
            handleDetailClose, handleTabChange, handleSort, exportToCSV
        },
        utils: {
            getPriorityColor, getStatusColor, getEscalationLevelStyle
        },
        options: {
            statusOptions, priorityOptions, issueTypeOptions, categoryOptions, escalationOptions, assignedToOptions
        },
        permissions: {
            can, is
        }
    }
}
